import React, {
  ReactNode,
  createContext,
  useState,
  useEffect,
  RefObject,
  useRef,
  Fragment,
  useContext,
} from "react";
import styled from "styled-components";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { createPortal } from "react-dom";
import { animated, useSpring } from "react-spring";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const AlertContext = createContext<RefObject<HTMLDivElement> | null>(null);

export function AlertProvider({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [currentRef, setRef] = useState<RefObject<HTMLDivElement> | null>(null);

  useEffect(() => {
    setRef(ref);
  }, []);

  return (
    <Fragment>
      <AlertContext.Provider value={currentRef}>{children}</AlertContext.Provider>
      <div ref={ref} data-test="alert" />
    </Fragment>
  );
}

export enum AlertType {
  success = "success",
  warning = "warning",
  error = "error",
}

export enum AlertDuration {
  "short" = 2000,
  "medium" = 6000,
  "mediumplus" = 7000,
  "long" = 10000,
}

// alertType is unused here because we need to filter it out
// eslint-disable-next-line
// @ts-ignore
const StyledAlert = styled(({ alertType, ...rest }) => <animated.div {...rest} />)<{
  alertType: AlertType;
}>`
  position: fixed;
  left: calc(50% - 40vw);
  width: 80vw;
  display: flex;
  align-items: center;
  z-index: 300;

  border-radius: ${({ theme }) => theme.deprecatedBorder.radius.large};
  padding: ${({ theme }) => theme.deprecatedSpacing.component.five};
  font-family: ${({ theme }) => theme.font.family.header};
  font-size: ${({ theme }) => theme.font.size.header.five};
  line-height: 22px;
  color: ${({ alertType, theme }) =>
    alertType === AlertType.success
      ? theme.deprecatedColors.white
      : alertType === AlertType.warning
      ? theme.deprecatedColors.text.normal
      : theme.deprecatedColors.white};
  box-shadow: ${({ theme }) => theme.deprecatedElevation.eight};
  background-color: ${({ alertType, theme }) =>
    alertType === AlertType.success
      ? theme.deprecatedColors.success
      : alertType === AlertType.warning
      ? theme.deprecatedColors.warning
      : theme.deprecatedColors.error};
`;

const StyledIcon = styled.div`
  width: 18px;
  height: 18px;
  margin-left: 4px;
  margin-right: 20px;
`;

interface AlertProps extends React.HTMLProps<HTMLObjectElement> {
  type: AlertType;
  className?: string;
  children: ReactNode;
  onDone?: () => void;
  duration?: AlertDuration;
}

export default function Alert({
  type,
  className,
  onDone,
  children,
  duration = AlertDuration.short,
}: AlertProps) {
  // we use this to track if the react component is mounted - we want to make sure we don't
  // call any state updates via react spring if the component has unmounted
  let _isMounted = false;
  const alertRef = useContext(AlertContext);
  const [show, setShow] = useState(true);
  const style = useSpring({
    from: { opacity: 1, bottom: "-100px" },
    // eslint-disable-next-line
    // @ts-ignore this is from the react-spring library, its correct
    to: async (next) => {
      await next({ opacity: 1, bottom: "60px" });
      // wait 2 sec before hiding
      // @TODO: is there a way to not do this in CI/test environment? - it slows down our tests
      await new Promise((resolve) => setTimeout(resolve, duration));
      await next({ opacity: 0, bottom: "-100px" });
    },
    onRest: () => {
      if (_isMounted) {
        setShow(false);
        if (onDone) {
          onDone();
        }
      }
    },
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    _isMounted = true;
    return () => {
      _isMounted = false;
    };
  });

  let icon: IconProp = "check-circle";
  switch (type) {
    case AlertType.success:
      icon = "check-circle";
      break;
    case AlertType.warning:
      icon = "exclamation-triangle";
      break;
    case AlertType.error:
      icon = "exclamation-triangle";
      break;
  }
  return show && alertRef && alertRef.current
    ? createPortal(
        <StyledAlert
          alertType={type}
          className={className}
          style={style}
          data-test="alert"
          data-test-alert-status={type}
        >
          <StyledIcon>
            <Icon icon={icon} />
          </StyledIcon>
          {children}
        </StyledAlert>,
        alertRef.current,
      )
    : null;
}
